import { adminMasterclassVaultDetailOptions, type AdminMasterclassCourseDetail } from '@doneverse/data/hooks'
import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import {
  CmsEditorHeaderActions,
  CmsEditorNotFound,
  CmsEditorShell,
  CmsEditorSkeleton,
} from './CmsEditorShell'
import type { CmsEditorTab } from './CmsEditorTabs'
import { PublishStatusBadge } from './PublishStatusBadge'

/**
 * The masterclass editor's chrome: loads the course by id, then hands it to the
 * tab body inside the shared `CmsEditorShell`.
 *
 * Keyed by `$courseId` rather than a slug, unlike the playbook editor. A course
 * slug is only unique within its page (`UNIQUE (page_slug, slug)`), and it is
 * editable up until publish — routing on it would break every open tab the
 * moment an author renamed a draft.
 */
const TABS: readonly CmsEditorTab[] = [
  { to: '/admin/masterclass-vault/$courseId', label: 'Details', exact: true },
  { to: '/admin/masterclass-vault/$courseId/lessons', label: 'Lessons' },
  { to: '/admin/masterclass-vault/$courseId/publish', label: 'Publish' },
]

export function MasterclassEditorShell({
  subtitle,
  children,
}: {
  subtitle: string
  children: (course: AdminMasterclassCourseDetail) => ReactNode
}) {
  const { courseId } = useParams({ strict: false })
  const { data: course, isLoading, isError } = useQuery(adminMasterclassVaultDetailOptions(courseId))

  if (isLoading) return <MasterclassEditorSkeleton />

  if (isError || !course) {
    return (
      <CmsEditorNotFound
        title="Masterclass"
        message="That course does not exist, or has been deleted."
        backTo="/admin/masterclass-vault"
        backLabel="Back to Masterclass Vault"
      />
    )
  }

  return (
    <CmsEditorShell
      title={course.title}
      badge={<PublishStatusBadge status={course.status} />}
      subtitle={subtitle}
      tabs={TABS}
      tabsLabel="Masterclass editor sections"
      params={{ courseId: course.id }}
      actions={
        <CmsEditorHeaderActions
          live={
            course.status === 'published'
              ? { to: '/masterclass-vault/$courseSlug', params: { courseSlug: course.slug } }
              : null
          }
          backTo="/admin/masterclass-vault"
          backLabel="All masterclasses"
        />
      }
    >
      {children(course)}
    </CmsEditorShell>
  )
}

export function MasterclassEditorSkeleton() {
  return <CmsEditorSkeleton tabCount={3} />
}
